import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FilterPills from "../../components/Common/Dropdowns/FilterPills";
// import { useUser } from '../../context/UserContext';
import axios from "axios";
import debounce from "lodash.debounce";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideFilter from "../../components/Common/SideFilter";
import { useStore } from "../../context/StoreContext";
import {
    autocomplete,
    buildSuggestionLabelLookup,
    formatSuggestionDisplayText,
    getSuggestionAheadText,
    getSuggestionCompletedValue,
    getSuggestionTabValue,
    isSearchEnabled,
    searchProducts,
} from "../../services/searchApi";
import {
    Browse,
    GridSkeleton,
    Pager,
    ProductCard,
} from "../../shared/components";
import { useStorefrontTheme } from "../../shared/theme/storefrontTheme";
import { fetchProductsWithFallback } from "../../utilities/apiHelper";
import * as API_END_POINT from "../../utilities/constant";
import { RECOMMENDED, STOREFRONT_PRODUCT_SORTS, MEILI_PRODUCT_SORT } from "../../utilities/productSorts";

// Opens on Recommended: the seller's featured products first.
const DEFAULT_SORT = RECOMMENDED;
const MEILI_SORT_MAP = MEILI_PRODUCT_SORT;

const getMatchedHighlightText = (hit) => {
    const matchedData = hit?.matchedData || {};
    const columns = String(hit?.matchedColumns || "")
        .split(",")
        .map((col) => col.trim())
        .filter(Boolean);

    for (const column of columns) {
        const value = matchedData[column];
        if (value) return value;
    }

    return (
        matchedData.brand_name ||
        matchedData.title ||
        hit?._formatted?.brand_name ||
        hit?._formatted?.title ||
        hit?.brand_name ||
        hit?.title ||
        ""
    );
};

const Products = () => {
    const { pageDetails } = useStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filtersView, setfiltersView] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 10,
    });
    const [isFilterPending, setIsFilterPending] = useState(true);
    const searchInputRef = useRef(null);
    const [limit] = useState(100);
    const [manualPriceRange, setManualPriceRange] = useState({
        min: "",
        max: "",
    });
    const [rawSearch, setRawSearch] = useState("");
    const [ghostText, setGhostText] = useState("");
    const suggestionFullTextRef = useRef("");

    const [searchTerm, setSearchTerm] = useState("");

    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [productFilters, setProductFilters] = useState(null);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT);

    const handleSortChange = (value) => {
        setSortBy(value);
    };

    const sortOptions = STOREFRONT_PRODUCT_SORTS;
    const suggestionLabelLookup = useMemo(
        () => buildSuggestionLabelLookup(productFilters),
        [productFilters],
    );
    const [filters, setFilters] = useState({
        brandValues: [],
        vendorValues: [],
        valueSearch: "",
        priceRange: [],
        animal_type: [],
        size: [],
        life_stage: [],
        tags: [],
        categories: [],
        discounts: [],
        sample: [],
    });
    const autocompleteCancelRef = useRef(null);
    const latestAutocompleteReqRef = useRef(0);
    const searchTermRef = useRef(searchTerm);
    const rawSearchRef = useRef(rawSearch);
    searchTermRef.current = searchTerm;
    rawSearchRef.current = rawSearch;

    const cancelInFlightAutocomplete = useCallback(() => {
        latestAutocompleteReqRef.current += 1;
        if (autocompleteCancelRef.current) {
            autocompleteCancelRef.current.cancel(
                "Cancelled due to new autocomplete request",
            );
            autocompleteCancelRef.current = null;
        }
    }, []);

    const debouncedSetSearch = useCallback(
        debounce((val) => {
            if (val !== searchTermRef.current) setSearchTerm(val);
        }, 500),
        [],
    );

    const runAutocomplete = useCallback(
        async (val, currentFilters, currentSortBy) => {
            if (!val || !isSearchEnabled() || /\s$/.test(val)) {
                cancelInFlightAutocomplete();
                suggestionFullTextRef.current = "";
                setGhostText("");
                return;
            }

            cancelInFlightAutocomplete();
            const requestId = latestAutocompleteReqRef.current;
            const source = axios.CancelToken.source();
            autocompleteCancelRef.current = source;

            try {
                const acFilterArr = [];
                acFilterArr.push({
                    field: "status",
                    operator: "in",
                    value: ["1"],
                });
                acFilterArr.push({
                    field: "shopify_status",
                    operator: "in",
                    value: ["active"],
                });
                if (currentFilters) {
                    if (currentFilters.priceRange?.length > 0) {
                        currentFilters.priceRange.forEach((value) => {
                            const min = value.min ?? 0;
                            const max = value.max ?? 999999;
                            acFilterArr.push({
                                field: "price",
                                operator: "between",
                                value: `${min} - ${max}`,
                            });
                        });
                    }
                    if (currentFilters.brandValues?.length > 0) {
                        acFilterArr.push({
                            field: "brand",
                            operator: "in",
                            value: currentFilters.brandValues.map((e) =>
                                e.toString(),
                            ),
                        });
                    }
                    if (currentFilters.vendorValues?.length > 0) {
                        acFilterArr.push({
                            field: "vendors",
                            options: {
                                operator: "in",
                                values: currentFilters.vendorValues.map((e) =>
                                    e.toString(),
                                ),
                            },
                        });
                    }
                    if (currentFilters.categories?.length > 0) {
                        const subcategory = [];
                        currentFilters.categories.forEach((c) => {
                            if (c?.subcategory?.length > 0)
                                subcategory.push(...c.subcategory);
                        });
                        if (subcategory.length > 0) {
                            acFilterArr.push({
                                field: "subcategory",
                                options: {
                                    operator: "in",
                                    values: subcategory.map((e) =>
                                        e.toString(),
                                    ),
                                },
                            });
                        }
                    }
                    if (currentFilters.tags?.length > 0) {
                        acFilterArr.push({
                            field: "tags",
                            options: {
                                operator: "in",
                                values: currentFilters.tags.map((e) =>
                                    e.toString(),
                                ),
                            },
                        });
                    }
                }
                const result = await autocomplete(val, {
                    limit: 1,
                    offset: 0,
                    sort: MEILI_SORT_MAP[currentSortBy],
                    filters: acFilterArr.length > 0 ? acFilterArr : undefined,
                    index: "products",
                    cancelToken: source.token,
                });

                if (requestId !== latestAutocompleteReqRef.current) return;
                if (val !== rawSearchRef.current) return;
                if (
                    searchInputRef.current &&
                    document.activeElement !== searchInputRef.current
                )
                    return;

                let suggestion = "";
                const hits = result?.data?.hits || result?.hits || [];
                if (Array.isArray(hits) && hits.length > 0) {
                    const highlightedText = getMatchedHighlightText(hits[0]);
                    const displayText = formatSuggestionDisplayText(
                        highlightedText,
                        suggestionLabelLookup,
                    );
                    suggestion = getSuggestionAheadText(
                        val,
                        highlightedText,
                        displayText,
                        suggestionLabelLookup,
                    );
                    suggestionFullTextRef.current = getSuggestionTabValue(
                        val,
                        highlightedText,
                        displayText,
                        suggestion,
                        suggestionLabelLookup,
                    );
                } else {
                    suggestionFullTextRef.current = "";
                }
                setGhostText(suggestion);
            } catch (error) {
                if (axios.isCancel(error)) return;
                if (requestId !== latestAutocompleteReqRef.current) return;
                suggestionFullTextRef.current = "";
                setGhostText("");
            }
        },
        [cancelInFlightAutocomplete, suggestionLabelLookup],
    );

    const debouncedAutocomplete = useCallback(
        debounce(runAutocomplete, 200, { maxWait: 600 }),
        [runAutocomplete],
    );

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
            debouncedAutocomplete.cancel();
            cancelInFlightAutocomplete();
        };
    }, [debouncedSetSearch, debouncedAutocomplete, cancelInFlightAutocomplete]);

    const handleSearchKeyDown = (e) => {
        if (e.key === "Tab" && (ghostText || suggestionFullTextRef.current)) {
            e.preventDefault();
            debouncedSetSearch.cancel();
            debouncedAutocomplete.cancel();
            cancelInFlightAutocomplete();
            const newValue = getSuggestionCompletedValue(
                rawSearch,
                ghostText,
                suggestionFullTextRef.current,
            );
            setRawSearch(newValue);
            setSearchTerm(newValue);
            suggestionFullTextRef.current = "";
            setGhostText("");
        } else if (e.key === "Escape") {
            suggestionFullTextRef.current = "";
            setGhostText("");
        }
    };
    const clearFilters = () => {
        setManualPriceRange({ min: "", max: "" });
        setSortBy(DEFAULT_SORT);
        setFilters({
            brandValues: [],
            vendorValues: [],
            valueSearch: "",
            priceRange: [],
            animal_type: [],
            size: [],
            life_stage: [],
            tags: [],
            categories: [],
            discounts: [],
            sample: [],
        });
        setSearchParams({});
    };

    useEffect(() => {
        getAllProductsFilter();
        // Only clear filters if there are no URL parameters at all
        if (
            !searchParams.get("category") &&
            !searchParams.get("brands") &&
            !searchParams.get("vendors") &&
            !searchParams.get("brand_values") &&
            !searchParams.get("hassample") &&
            !searchParams.get("price") &&
            !searchParams.get("animal_type") &&
            !searchParams.get("life_stage_age") &&
            !searchParams.get("specialty_diet") &&
            !searchParams.get("size_breed") &&
            !searchParams.get("intended_use")
        ) {
            clearFilters();
            setfiltersView([]);
        }
    }, [searchParams]);

    const getAllProductsFilter = async () => {
        const response = await axios.post(
            `${API_END_POINT.PRODUCT_FILTERS}`,
            {},
        );
        setProductFilters(response.data.data?.availableFilters);
    };

    const cancelReq = useRef(null);
    const latestReqRef = useRef(0);
    const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
    /* The page belongs to the query it was turned on: a new filter, sort or
       search starts again at page 1. Derived rather than reset in an effect, so
       a query change fetches once — not once on the stale page, then again on
       page 1. */
    const queryKey = `${filtersKey}|${sortBy}|${searchTerm}`;
    const [pageOf, setPageOf] = useState({ key: queryKey, page: 1 });
    const currentPage = pageOf.key === queryKey ? pageOf.page : 1;
    const offset = (currentPage - 1) * limit;
    const handlePageChange = (page) => {
        setPageOf({ key: queryKey, page });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const fetchProductList = useCallback(async () => {
        setIsLoading(true);
        latestReqRef.current += 1;
        const requestId = latestReqRef.current;

        // Cancel previous request if it exists
        if (cancelReq?.current) {
            cancelReq?.current?.cancel("Cancelled due to new request");
        }

        // Create new cancel token SOURCE (IMPORTANT)
        const source = axios.CancelToken.source();
        cancelReq.current = source;
        try {
            const isSearchEnabledFlag = isSearchEnabled();

            if (isSearchEnabledFlag) {
                // Use new API (Meilisearch or filtered API)
                const filterArr = [];
                filterArr.push({
                    field: "status",
                    operator: "in",
                    value: ["1"],
                });
                filterArr.push({
                    field: "shopify_status",
                    operator: "in",
                    value: ["active"],
                });
                const searchKeyword = searchTerm;
                if (filters.priceRange && filters.priceRange.length > 0) {
                    filters.priceRange.forEach((value) => {
                        const min = value.min ?? 0;
                        const max = value.max ?? 999999;
                        const obj = {
                            field: "price",
                            operator: "between",
                            value: `${min} - ${max}`,
                        };
                        filterArr.push(obj);
                    });
                }
                if (filters.brandValues && filters.brandValues.length > 0) {
                    const obj = {
                        field: "brand",
                        operator: "in",
                        value: filters.brandValues.map((e) => e.toString()),
                    };
                    filterArr.push(obj);
                }
                if (filters.vendorValues && filters.vendorValues.length > 0) {
                    const obj = {
                        field: "vendors",
                        options: {
                            operator: "in",
                            values: filters.vendorValues.map((e) =>
                                e.toString(),
                            ),
                        },
                    };
                    filterArr.push(obj);
                }
                if (filters.categories && filters.categories.length > 0) {
                    let subcategory = [];
                    filters.categories.forEach((category) => {
                        if (category?.subcategory?.length > 0) {
                            subcategory.push(...category?.subcategory);
                        }
                    });
                    const obj = {
                        field: "subcategory",
                        options: {
                            operator: "in",
                            values: subcategory.map((e) => e.toString()),
                        },
                    };
                    filterArr.push(obj);
                }
                if (filters.tags && filters.tags.length > 0) {
                    const obj = {
                        field: "tags",
                        options: {
                            operator: "in",
                            values: filters.tags.map((e) =>
                                e === "small_pet"
                                    ? "small_animal"
                                    : e.toString(),
                            ),
                        },
                    };
                    filterArr.push(obj);
                }

                // Use Meilisearch when enabled and a search keyword is present
                const meiliSort = MEILI_SORT_MAP[sortBy] || undefined;

                const searchResult = await searchProducts(searchKeyword, {
                    filters: filterArr.length > 0 ? filterArr : undefined,
                    sort: meiliSort,
                    useFeaturedSort: sortBy === RECOMMENDED,
                    limit,
                    offset,
                    cancelToken: source.token,
                });

                if (requestId !== latestReqRef.current) return;

                if (
                    searchResult?.data &&
                    (searchResult.data.items || searchResult.data.records)
                ) {
                    const items =
                        searchResult.data.items ||
                        searchResult.data.records ||
                        [];
                    const pagination = searchResult.data.pagination || {};
                    const totalItems =
                        pagination.totalItems ??
                        searchResult.data.count ??
                        items.length;
                    setProducts(items);
                    setPagination({
                        currentPage: pagination.currentPage || currentPage,
                        totalItems,
                        totalPages:
                            pagination.totalPages ||
                            Math.ceil(totalItems / limit),
                        itemsPerPage: pagination.itemsPerPage || limit,
                    });
                }
            } else {
                // No filters and no search: use fallback API (primary → old API)
                const response = await fetchProductsWithFallback({
                    paging: { limit: limit, offset: offset },
                    sort: [{ fieldName: "created_at", fieldOrder: "DESC" }],
                });
                if (requestId !== latestReqRef.current) return;
                setProducts(response.data.data.items);
                setPagination(response.data.data.pagination);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            if (requestId === latestReqRef.current) {
                setProducts([]);
                setPagination({ currentPage: 1, totalPages: 0, totalItems: 0 });
            }
        } finally {
            if (requestId === latestReqRef.current) {
                setTimeout(() => {
                    setIsLoading(false);
                }, [1500]);
            }
        }
    }, [
        filtersKey,
        sortBy,
        currentPage,
        searchTerm,
        limit,
        offset,
    ]);
    const firstRender = useRef(true);

    useEffect(() => {
        // if (firstRender.current) {
        //     firstRender.current = false;
        //     return;
        // }
        fetchProductList();
        setTimeout(() => {
            setIsFilterPending(false);
        }, 1500);
    }, [fetchProductList]);

    const getTagName = () => {
        if (searchParams.get("category")) {
            const finame = productFilters?.categories.find(
                (cate) => cate.code == searchParams.get("category"),
            );
            return finame?.name;
        } else {
            return "Shop All";
        }
    };

    /* No PageLayout: it wraps children in `max-w-7xl mx-auto` and renders its
       own breadcrumb and heading, while the reference's `.br-wrap` is 1240px
       with a 40px gutter and owns the crumb and H1. */
    const crumb = (() => {
        const category = searchParams.get("category");
        if (!category)
            return [{ label: "Home", href: "/" }, { label: "Shop All" }];
        const cat = productFilters?.categories?.find((c) => c.code == category);
        const sub = cat?.subCategories?.find(
            (c) => c.code == searchParams.get("subcategory"),
        );
        return [
            { label: "Home", href: "/" },
            { label: "Shop All", href: "/all-products" },
            ...(cat?.name ? [{ label: cat.name }] : []),
            ...(sub?.name ? [{ label: sub.name }] : []),
        ];
    })();

    /* The grid waits for the products AND the facets: a full grid beside an
       empty sidebar reads as a store with no filters. */
    const isBusy =
        isLoading || isFilterPending || !productFilters?.categories?.length;

    /* The partner owns the skin: `.sfx` resolves the static tokens, but --o
       (anything clickable) and --m (header and banner) come from the saved Style
       config and have to be written onto the element, or every orange on the
       page is ours rather than theirs. */
    const sfxRef = useStorefrontTheme(
        pageDetails?.page_data?.colorAndApperances,
    );

    /* ⛔ No `font-family-inter` on this wrapper: that class is
       `font-family: 'Inter', sans-serif !important` (src/common.css:511) and
       overrode the design system's type for the whole page. Dropping the class
       is not what fixes it — the `!important` lives on `StoreLayout`, so this
       subtree would still inherit Inter; `.sfx` declares the typeface itself.
       See the note in storefront-tokens.css. */
    return (
        <div ref={sfxRef} className="sfx">
            <Browse
                title={getTagName()}
                crumb={crumb}
                noun="products"
                total={pagination?.totalItems}
                items={isBusy ? [] : products}
                loading={isBusy}
                loadingView={<GridSkeleton count={8} />}
                searchRef={searchInputRef}
                searchValue={rawSearch}
                searchPlaceholder="Search products"
                onSearchChange={(val) => {
                    setRawSearch(val);
                    debouncedSetSearch(val);
                    if (val && !/\s$/.test(val)) {
                        debouncedAutocomplete(val, filters, sortBy);
                    } else {
                        debouncedAutocomplete.cancel();
                        cancelInFlightAutocomplete();
                        suggestionFullTextRef.current = "";
                        setGhostText("");
                    }
                }}
                onSearchKeyDown={handleSearchKeyDown}
                onSearchBlur={() => {
                    suggestionFullTextRef.current = "";
                    setGhostText("");
                }}
                sorts={sortOptions.map((o) => [o, o])}
                sortValue={sortBy}
                onSortChange={handleSortChange}
                chipsSlot={
                    filtersView?.length > 0 ? (
                        <FilterPills
                            filterData={productFilters}
                            setManualPriceRange={setManualPriceRange}
                            clearFilters={clearFilters}
                            filtersView={filtersView}
                            setfiltersView={setfiltersView}
                            setFilters={setFilters}
                        />
                    ) : null
                }
                chipCount={filtersView?.length || 0}
                rail={
                    <SideFilter
                        setManualPriceRange={setManualPriceRange}
                        manualPriceRange={manualPriceRange}
                        filterData={productFilters}
                        filtersView={filtersView}
                        setfiltersView={setfiltersView}
                        filters={filters}
                        setFilters={setFilters}
                    />
                }
                belowGrid={
                    isBusy ? null : (
                        <Pager
                            page={currentPage}
                            perPage={limit}
                            total={pagination?.totalItems}
                            onPageChange={handlePageChange}
                        />
                    )
                }
                emptyTitle="Nothing matches those filters"
                emptyBody="Try removing one, or clear them all to see everything we carry."
                onClearAll={clearFilters}
                renderItem={(product) => {
                    /* The main image first; the rest are the product page's. */
                    const images = Array.isArray(product.product_images)
                        ? product.product_images
                        : [];
                    const main = [...images].sort(
                        (a, b) => Number(b.is_main) - Number(a.is_main),
                    )[0];

                    const isVendor = product?.is_multi_brand;
                    const brand = isVendor
                        ? product.vendor?.name ||
                          product.shopify_vendor?.shopdot_vendor?.display_name
                        : product.brand?.name || product.brand_name;
                    const brandId = product.brand?.id;
                    const cat = searchParams.get("category");

                    return (
                        <ProductCard
                            name={product.title}
                            brand={brand}
                            price={product.price_msrp}
                            image={main?.src}
                            /* A real href, so the card is a real link:
                               keyboard reachable, announced and openable in a
                               new tab. The router still intercepts the click. */
                            href={`/product/${product.id}/${cat ? `?category=${cat}` : ""}`}
                            onClick={(e) => {
                                if (
                                    e.metaKey ||
                                    e.ctrlKey ||
                                    e.shiftKey ||
                                    e.button === 1
                                )
                                    return;
                                e.preventDefault();
                                navigate(
                                    `/product/${product.id}/${cat ? `?category=${cat}` : ""}`,
                                );
                            }}
                            brandHref={
                                brandId
                                    ? `/brands/${brandId}${isVendor ? `/vendor/${product?.vendor?.id}/` : "/"}`
                                    : undefined
                            }
                            onBrandClick={
                                brandId
                                    ? (e) => {
                                          if (
                                              e.metaKey ||
                                              e.ctrlKey ||
                                              e.shiftKey ||
                                              e.button === 1
                                          )
                                              return;
                                          e.preventDefault();
                                          navigate(
                                              `/brands/${brandId}${isVendor ? `/vendor/${product?.vendor?.id}/` : "/"}`,
                                          );
                                      }
                                    : undefined
                            }
                            /* `contain`, not the reference's `cover`: SP-main
                               fills the frame, which suits a shelf shot to one
                               brief, but this catalogue carries packshots that
                               `cover` crops. A per-catalogue call — flip it once
                               a store's photography is consistent. */
                            objectFit="contain"
                        />
                    );
                }}
            />
        </div>
    );
};

export default Products;
